import { ReactNode } from 'react';

import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';

import {
  createResourceLinkExtension,
  extractLastNameComponent,
  isResourceName,
  useFeatureFlag,
} from '@spotify/confidence-core-react';

import { metricsPlugin } from '../plugin';
import { measurementRouteRef, metricRouteRef } from '../routes';

interface MetricResourceLinkProps {
  name: string;
  text: ReactNode;
  className?: string;
}

const MetricResourceLink = ({
  name,
  text,
  className,
}: MetricResourceLinkProps) => {
  const routeRef = useRouteRef(metricRouteRef);
  const metricId = extractLastNameComponent(name);

  if (!routeRef || !metricId) {
    return <>{text}</>;
  }

  const to = routeRef({ id: metricId });

  return (
    <Link to={to} className={className}>
      {text}
    </Link>
  );
};

export const MetricResourceLinkExtension = metricsPlugin.provide(
  createResourceLinkExtension({
    id: 'metric-resource-link',
    predicate: ({ name }) => isResourceName(name, 'metrics'),
    icon: 'metric',
    render: ({ name, text, className }) => (
      <MetricResourceLink name={name} text={text} className={className} />
    ),
  }),
);

interface MeasurementResourceLinkProps {
  name: string;
  text: ReactNode;
  className?: string;
}

const MeasurementResourceLink = ({
  name,
  text,
  className,
}: MeasurementResourceLinkProps) => {
  const routeRef = useRouteRef(measurementRouteRef);
  const measurementId = extractLastNameComponent(name);
  const { enabled: measurementsEnabled } = useFeatureFlag<{ enabled: boolean }>(
    'measurements',
    { enabled: false },
  );

  // The measurement detail page is gated behind the `measurements` flag. When
  // it isn't reachable, render plain text instead of a link to a dead page.
  if (!routeRef || !measurementId || !measurementsEnabled) {
    return <>{text}</>;
  }

  const to = routeRef({ id: measurementId });

  return (
    <Link to={to} className={className}>
      {text}
    </Link>
  );
};

export const MeasurementResourceLinkExtension = metricsPlugin.provide(
  createResourceLinkExtension({
    id: 'measurement-resource-link',
    predicate: ({ name }) => isResourceName(name, 'measurements'),
    icon: 'measurement',
    render: ({ name, text, className }) => (
      <MeasurementResourceLink name={name} text={text} className={className} />
    ),
  }),
);
